import { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import {
    Box,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Typography,
    CircularProgress,
    Alert,
    Button,
    TextField,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { Grid } from '../../../../UniversalComponents'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { getAvailableVariables, getBoatTelemetry } from '../../../../../apiRoutes'
import { useToastMessage } from '../../../../../context/ToastPopUpContext'
import Plot from 'react-plotly.js'

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#8cc63f' },
        secondary: { main: '#4caf50' },
        background: { default: '#1a1a1a', paper: '#2d2d2d' },
        text: { primary: '#ffffff', secondary: '#b0b0b0' },
    },
})

const MAX_HISTORY = 10000 // Increased limit to show more historical data
const MIN_REFRESH_INTERVAL = 10 // 1 minute minimum refresh interval

type ChartPoint = { timestamp: string; value: number }
type TimeFilter = '1hour' | '1day' | '1week' | 'custom'

type CustomChartViewProps = {
    row: { deviceSerial?: string; device?: string }
}

const CustomChartView = forwardRef<any, CustomChartViewProps>(({ row }, ref) => {
    const setToastMessage = useToastMessage()
    const [availableVariables, setAvailableVariables] = useState<string[]>([])
    const [selectedVariables, setSelectedVariables] = useState<string[]>([])
    const [chartData, setChartData] = useState<{ [key: string]: ChartPoint[] }>({})
    const [loading, setLoading] = useState(false)
    const [variablesLoading, setVariablesLoading] = useState(false)
    const [timeFilter, setTimeFilter] = useState<TimeFilter>('1day')
    const [customStartTime, setCustomStartTime] = useState<any>(null)
    const [customEndTime, setCustomEndTime] = useState<any>(null)
    const [lastRefreshTime, setLastRefreshTime] = useState<number>(0)
    const [isRefreshing, setIsRefreshing] = useState(false)

    const deviceId = row?.deviceSerial || row?.device

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        handleRefresh: handleRefresh
    }))

    // Get time range based on selected filter
    const getTimeRange = () => {
        const now = new Date()
        switch (timeFilter) {
            case '1hour':
                return {
                    startTime: new Date(now.getTime() - 60 * 60 * 1000),
                    endTime: now
                }
            case '1day':
                return {
                    startTime: new Date(now.getTime() - 24 * 60 * 60 * 1000),
                    endTime: now
                }
            case '1week':
                return {
                    startTime: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
                    endTime: now
                }
            case 'custom':
                return {
                    startTime: customStartTime ? customStartTime.toDate() : new Date(now.getTime() - 24 * 60 * 60 * 1000),
                    endTime: customEndTime ? customEndTime.toDate() : now
                }
            default:
                return {
                    startTime: new Date(now.getTime() - 24 * 60 * 60 * 1000),
                    endTime: now
                }
        }
    }

    // Fetch available variables
    const fetchAvailableVariables = async () => {
        if (!deviceId) {
            setToastMessage('No device paired with this boat.', 'info')
            return
        }

        setVariablesLoading(true)
        try {
            const response = await getAvailableVariables(deviceId)
            if (response?.variables) {
                setAvailableVariables(response.variables)
            } else {
                setToastMessage('No variables available for this device.', 'info')
            }
        } catch (error: any) {
            setToastMessage(error.message || 'Failed to fetch available variables.', 'error')
            console.error('Error fetching variables:', error)
        } finally {
            setVariablesLoading(false)
        }
    }

    // Fetch telemetry data for selected variables
    const fetchTelemetryData = async () => {
        if (!deviceId || selectedVariables.length === 0) return

        setLoading(true)
        try {
            const { startTime, endTime } = getTimeRange()
            
            const response = await getBoatTelemetry({
                deviceSerial: deviceId,
                keys: selectedVariables,
                startTime: startTime.toISOString(),
                endTime: endTime.toISOString(),
            })

            
            if (Object.keys(response).length === 0) {
                setToastMessage('No telemetry data available for selected variables.', 'info')
                return
            }

            setChartData((prev) => {
                const updated: { [key: string]: ChartPoint[] } = {}

                Object.entries(response).forEach(([timestamp, variables]) => {
                    Object.entries(variables as { [key: string]: any }).forEach(([key, values]) => {
                        // Ensure values is an array
                        if (!Array.isArray(values)) {
                            console.warn(`Values for ${key} is not an array:`, values)
                            return
                        }

                        const numericValues = values
                            .map((val) => {
                                if (typeof val === 'string') {
                                    return parseFloat(val.replace(/[^\d.-]/g, ''))
                                } else if (typeof val === 'number') {
                                    return val
                                }
                                return NaN
                            })
                            .filter((val) => !isNaN(val))

                        if (numericValues.length === 0) return
                        const avgValue = numericValues.reduce((a, b) => a + b, 0) / numericValues.length

                        if (!updated[key]) updated[key] = []
                        updated[key].push({ timestamp, value: avgValue })
                    })
                })

                // Sort and limit data
                Object.keys(updated).forEach((key) => {
                    updated[key] = updated[key]
                        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                        .slice(0, MAX_HISTORY) // Keep first MAX_HISTORY points (chronological order)
                })


                return updated
            })
        } catch (error: any) {
            setToastMessage(error.message || 'Failed to fetch telemetry data.', 'error')
            console.error('Error fetching telemetry:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (deviceId) {
            fetchAvailableVariables()
        }
    }, [deviceId])

    useEffect(() => {
        if (selectedVariables.length > 0) {
            fetchTelemetryData()
        }
    }, [selectedVariables, timeFilter, customStartTime, customEndTime])

    const handleVariableChange = (event: any) => {
        setSelectedVariables(event.target.value)
    }

    const handleRemoveVariable = (variableToRemove: string) => {
        setSelectedVariables(prev => prev.filter(v => v !== variableToRemove))
    }

    const handleTimeFilterChange = (event: any) => {
        setTimeFilter(event.target.value)
    }

    const handleRefresh = async () => {
        const now = Date.now()
        const timeSinceLastRefresh = now - lastRefreshTime
        
        if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
            const remainingTime = Math.ceil((MIN_REFRESH_INTERVAL - timeSinceLastRefresh) / 1000)
            setToastMessage(`Please wait ${remainingTime} seconds before refreshing again.`, 'warning')
            return
        }

        if (selectedVariables.length === 0) {
            setToastMessage('Please select variables to refresh data.', 'info')
            return
        }

        setIsRefreshing(true)
        setLastRefreshTime(now)
        await fetchTelemetryData()
        setIsRefreshing(false)
    }

    // Prepare chart traces with independent scaling for each variable
    const prepareChartTraces = () => {
        const colors = ['#FF5722', '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#8BC34A']
        const traces: any[] = []

        // Process each variable independently
        selectedVariables.forEach((variable, index) => {
            const data = chartData[variable] || []
            
            if (!Array.isArray(data) || data.length === 0) {
                return
            }

            // Filter valid data points
            const validData = data.filter(d => 
                d && 
                typeof d.timestamp === 'string' && 
                typeof d.value === 'number' && 
                !isNaN(d.value)
            )

            if (validData.length === 0) {
                return
            }

            // Calculate min and max for this specific variable
            const values = validData.map(d => d.value)
            const variableMin = Math.min(...values)
            const variableMax = Math.max(...values)
            const variableRange = variableMax - variableMin

            // Normalize this variable's values to 0-1 range independently
            const normalizedData = validData.map(d => ({
                ...d,
                normalizedValue: variableRange > 0 ? (d.value - variableMin) / variableRange : 0.5
            }))

            traces.push({
                type: 'scatter',
                mode: 'lines+markers',
                x: normalizedData.map(d => d.timestamp),
                y: normalizedData.map(d => d.normalizedValue),
                name: variable,
                marker: {
                    color: colors[index % colors.length],
                    size: 4,
                    opacity: 0.7,
                },
                line: {
                    color: colors[index % colors.length],
                    width: 2,
                },
                hovertemplate: `<b>${variable}</b><br><b>Time:</b> %{x}<br><b>Value:</b> %{customdata}<br><b>Range:</b> ${variableMin.toFixed(2)} - ${variableMax.toFixed(2)}<extra></extra>`,
                customdata: validData.map(d => d.value),
            })
        })

        return { traces }
    }

    const { traces } = prepareChartTraces()

    // Create layout with clean y-axis (no labels)
    const createLayout = () => {
        const layout: any = {
            height: 600,
            margin: { t: 20, r: 20, l: 40, b: 40 },
            paper_bgcolor: '#383838',
            plot_bgcolor: '#383838',
            font: {
                color: 'white',
                family: 'Roboto, sans-serif',
                size: 10,
            },
            xaxis: {
                gridcolor: '#555555',
                type: 'date',
                tickformat: '%b %d, %Y %H:%M',
                tickfont: { color: 'white', size: 9 },
            },
            yaxis: {
                gridcolor: '#555555',
                range: [0, 1],
                showticklabels: false,
                showgrid: true,
                zeroline: false,
            },
            showlegend: true,
            legend: {
                x: 0.02,
                y: 0.98,
                bgcolor: 'rgba(0,0,0,0.5)',
                bordercolor: 'rgba(255,255,255,0.2)',
                borderwidth: 1,
                font: { color: 'white', size: 9 },
            },
        }

        return layout
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '50vh', }}>
                <Box sx={{ p: 3 }}>
                    <Paper sx={{ bgcolor: 'background.default', p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                Custom Chart Configuration
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleRefresh}
                                disabled={isRefreshing || selectedVariables.length === 0}
                                startIcon={isRefreshing ? <CircularProgress size={16} /> : <Typography>↻</Typography>}
                                sx={{ minWidth: '120px' }}
                            >
                                {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                            </Button>
                        </Box>
                        
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="variables-select-label">Select Variables</InputLabel>
                                    <Select
                                        labelId="variables-select-label"
                                        multiple
                                        value={selectedVariables}
                                        onChange={handleVariableChange}
                                        label="Select Variables"
                                        disabled={variablesLoading}
                                    >
                                        {availableVariables.map((variable) => (
                                            <MenuItem key={variable} value={variable}>
                                                {variable}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="time-filter-label">Time Range</InputLabel>
                                    <Select
                                        labelId="time-filter-label"
                                        value={timeFilter}
                                        onChange={handleTimeFilterChange}
                                        label="Time Range"
                                    >
                                        <MenuItem value="1hour">Last 1 Hour</MenuItem>
                                        <MenuItem value="1day">Last 1 Day</MenuItem>
                                        <MenuItem value="1week">Last 1 Week</MenuItem>
                                        <MenuItem value="custom">Custom Range</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedVariables.map((variable) => (
                                        <Chip
                                            key={variable}
                                            label={variable}
                                            onDelete={() => handleRemoveVariable(variable)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>

                        {timeFilter === 'custom' && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12} md={6}>
                                        <DateTimePicker
                                            label="Start Time"
                                            value={customStartTime}
                                            onChange={(newValue) => setCustomStartTime(newValue)}
                                            slotProps={{
                                                textField: { fullWidth: true }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DateTimePicker
                                            label="End Time"
                                            value={customEndTime}
                                            onChange={(newValue) => setCustomEndTime(newValue)}
                                            slotProps={{
                                                textField: { fullWidth: true }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </LocalizationProvider>
                        )}

                        {variablesLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                    Loading available variables...
                                </Typography>
                            </Box>
                        )}

                        {!deviceId && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                No device paired with this boat.
                            </Alert>
                        )}
                    </Paper>

                    {selectedVariables.length > 0 && (
                        <Paper sx={{ bgcolor: 'background.default', p: 2 }}>
                            {/* <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Custom Chart
                            </Typography> */}
                            
                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" sx={{ ml: 2 }}>
                                        Loading chart data...
                                    </Typography>
                                </Box>
                            )}

                            {traces.length > 0 ? (
                                <Plot
                                    data={traces}
                                    layout={createLayout()}
                                    config={{ displayModeBar: true, responsive: true }}
                                    useResizeHandler={true}
                                    style={{ width: '100%', height: '600px' }}
                                    onError={(err) => console.error('Plot error:', err)}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        height: '400px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="body1" color="text.secondary">
                                        Select variables to display chart
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    )
})

export default CustomChartView
